import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, Checkbox, FormControlLabel, Stack, Chip, CircularProgress, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  createVaultMaster, getVaultMasters, updateVaultMaster, deleteVaultMaster,
  getVaultResetRequests, approveVaultReset,
} from "../../services/vault.service";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "file", label: "File upload" },
  { value: "password", label: "Password" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select" },
];
const OPTION_TYPES = ["dropdown", "multiselect"];

const emptyField = () => ({ label: "", type: "text", required: false, options: [] });

export default function VaultPage() {
  const navigate = useNavigate();
  const isAdmin = useMemo(() => {
    try {
      const d = jwtDecode(localStorage.getItem("token"));
      return d.role === 1 || d.role === 4;
    } catch {
      return false;
    }
  }, []);

  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [fields, setFields] = useState([emptyField()]);
  const [resetOpen, setResetOpen] = useState(false);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getVaultMasters();
      setMasters(res?.data || []);
    } catch (e) {
      console.error("load masters failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (i, patch) => setFields((p) => p.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const addField = () => setFields((p) => [...p, emptyField()]);
  const removeField = (i) => setFields((p) => p.filter((_, idx) => idx !== i));
  // Changing to an option type seeds one empty option so the editor shows a row to fill.
  const changeType = (i, type) =>
    setFields((p) => p.map((f, idx) =>
      idx === i ? { ...f, type, options: OPTION_TYPES.includes(type) && !(f.options?.length) ? [""] : f.options } : f));
  const setOption = (i, oi, val) =>
    setFields((p) => p.map((f, idx) => (idx === i ? { ...f, options: f.options.map((o, k) => (k === oi ? val : o)) } : f)));
  const addOption = (i) =>
    setFields((p) => p.map((f, idx) => (idx === i ? { ...f, options: [...(f.options || []), ""] } : f)));
  const removeOption = (i, oi) =>
    setFields((p) => p.map((f, idx) => (idx === i ? { ...f, options: f.options.filter((_, k) => k !== oi) } : f)));

  const openCreate = () => {
    setEditingId(null); setTitle(""); setFields([emptyField()]); setOpen(true);
  };
  const openEdit = (e, m) => {
    e.stopPropagation();
    setEditingId(m._id);
    setTitle(m.title);
    setFields(m.fields.length ? m.fields.map((f) => ({ label: f.label, type: f.type, required: !!f.required, options: [...(f.options || [])] })) : [emptyField()]);
    setOpen(true);
  };

  const save = async () => {
    const clean = fields.filter((f) => f.label.trim()).map((f) => ({
      label: f.label.trim(), type: f.type, required: !!f.required,
      options: OPTION_TYPES.includes(f.type)
        ? (f.options || []).map((o) => String(o).trim()).filter(Boolean)
        : [],
    }));
    if (!title.trim()) return alert("Title required");
    if (!clean.length) return alert("Add at least one field");
    setSaving(true);
    try {
      const payload = { title: title.trim(), fields: clean };
      if (editingId) await updateVaultMaster(editingId, payload);
      else await createVaultMaster(payload);
      setOpen(false);
      load();
    } catch (e) {
      console.error("save master failed", e);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${m.title}" and all its data?`)) return;
    try { await deleteVaultMaster(m._id); load(); } catch (err) { console.error(err); }
  };

  const openResetRequests = async () => {
    setResetOpen(true);
    try {
      const res = await getVaultResetRequests();
      setRequests(res?.data || []);
    } catch (e) {
      console.error("load reset requests failed", e);
    }
  };
  const approve = async (userId) => {
    try {
      await approveVaultReset(userId);
      setRequests((p) => p.filter((r) => String(r.userId) !== String(userId)));
    } catch (e) {
      console.error("approve failed", e);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Vault</Typography>
        {isAdmin && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<LockResetIcon />} onClick={openResetRequests}
              sx={{ borderColor: "#008080", color: "#008080" }}>Password Reset</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
              sx={{ bgcolor: "#008080", "&:hover": { bgcolor: "#006666" } }}>New Master</Button>
          </Stack>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : masters.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center", color: "text.secondary" }}>
          {isAdmin ? <>No masters yet. Click <b>New Master</b> to create one (e.g. "AWS").</>
            : "No vault items yet."}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ pl: "16px !important" }}><b>Title</b></TableCell>
                <TableCell sx={{ pl: "16px !important" }}><b>Fields</b></TableCell>
                <TableCell align="center"><b>Records</b></TableCell>
                {isAdmin && <TableCell align="right"><b>Actions</b></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {masters.map((m) => (
                <TableRow key={m._id} hover sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/home/vault/${m._id}`)}>
                  <TableCell sx={{ pl: "16px !important" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FolderIcon sx={{ color: "#008080" }} /><b>{m.title}</b>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ pl: "16px !important" }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {m.fields.map((f, i) => (
                        <Chip key={i} size="small" label={`${f.label} (${f.type})`} variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{m.recordCount}</TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => openEdit(e, m)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete master">
                        <IconButton size="small" color="error" onClick={(e) => remove(e, m)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit master dialog (admin only) — title + fields. No access here; access is
          granted per-record inside the master. */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? "Edit Master" : "New Master"}</DialogTitle>
        <DialogContent dividers>
          <TextField label="Title (e.g. AWS)" fullWidth value={title}
            onChange={(e) => setTitle(e.target.value)} sx={{ mb: 3 }} autoFocus />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Fields</Typography>
          <Stack spacing={1.5}>
            {fields.map((f, i) => (
              <Box key={i} sx={{ border: "1px solid #eee", borderRadius: 1, p: 1.25 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField label="Field label" size="small" value={f.label}
                    onChange={(e) => setField(i, { label: e.target.value })} sx={{ flex: 1 }} />
                  <Select size="small" value={f.type} onChange={(e) => changeType(i, e.target.value)}
                    sx={{ width: 150 }}>
                    {FIELD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                  <FormControlLabel control={<Checkbox checked={f.required}
                    onChange={(e) => setField(i, { required: e.target.checked })} />} label="Req" />
                  <IconButton size="small" color="error" onClick={() => removeField(i)}
                    disabled={fields.length === 1}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
                {OPTION_TYPES.includes(f.type) && (
                  <Box sx={{ mt: 1, pl: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Options:</Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                      {(f.options || []).map((opt, oi) => (
                        <Stack key={oi} direction="row" spacing={1} alignItems="center">
                          <TextField size="small" placeholder={`Option ${oi + 1}`} value={opt}
                            onChange={(e) => setOption(i, oi, e.target.value)} sx={{ width: 280 }} />
                          <IconButton size="small" color="error" onClick={() => removeOption(i, oi)}
                            disabled={(f.options || []).length === 1}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                      ))}
                    </Stack>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(i)} sx={{ mt: 0.5 }}>
                      Add option
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
          <Button startIcon={<AddIcon />} onClick={addField} sx={{ mt: 1.5 }}>Add Field</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            sx={{ bgcolor: "#008080", "&:hover": { bgcolor: "#006666" } }}>
            {saving ? "Saving..." : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password reset requests (admin) */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vault Password Reset Requests</DialogTitle>
        <DialogContent dividers>
          {requests.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No pending requests.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {requests.map((r) => (
                <Stack key={String(r.userId)} direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ p: 1.25, border: "1px solid #eee", borderRadius: 1 }}>
                  <Box>
                    <b>{r.name}</b>
                    <Typography variant="caption" color="text.secondary" display="block">{r.email}</Typography>
                  </Box>
                  <Button size="small" variant="contained" onClick={() => approve(r.userId)}
                    sx={{ bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}>Approve</Button>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
